// ============================================
// NarbodicaProfileOnboarding
// ============================================
// Universal onboarding flow for all Narbodica apps.
// Replaces the old NarbodicaSync cross-app wall.
//
// Flow:
//   1. Load NarbodicaProfile from /api/narbodica-profile/for-app/:app
//   2. Show "here's what we already know" — confirm or edit
//   3. Ask only for genuinely missing fields
//   4. Photo step — show existing photos, pick which to use, add more (max 6)
//   5. Write all new data back to NarbodicaProfile
//   6. Mark onboarding complete for this app
//
// Usage:
//   <NarbodicaProfileOnboarding
//     app="connect"
//     appLabel="Connect"
//     accentColor="#2dd4bf"
//     apiBase="https://..."
//     token="..."
//     onComplete={() => {}}
//   />
// ============================================

import { useState, useEffect, useCallback } from 'react';

const API = (base, path) => `${base}/api${path}`;

const S = {
  page:    { minHeight: '100dvh', background: '#09090b', fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  card:    { width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 20 },
  title:   { color: '#fafafa', fontSize: 26, fontWeight: 700, fontFamily: "var(--font-serif)", margin: 0 },
  sub:     { color: '#71717a', fontSize: 14, margin: 0, lineHeight: 1.6 },
  label:   { color: '#a1a1aa', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6, display: 'block' },
  input:   { width: '100%', background: '#18181b', border: '1px solid #3f3f46', borderRadius: 10, padding: '12px 14px', color: '#fafafa', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" },
  textarea:{ width: '100%', background: '#18181b', border: '1px solid #3f3f46', borderRadius: 10, padding: '12px 14px', color: '#fafafa', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" },
  chip:    (sel, accent) => ({ padding: '8px 14px', borderRadius: 30, cursor: 'pointer', border: `2px solid ${sel ? accent : '#27272a'}`, background: sel ? `${accent}15` : '#18181b', color: sel ? '#fafafa' : '#71717a', fontSize: 13, fontWeight: sel ? 600 : 400, transition: 'all 0.15s', userSelect: 'none', fontFamily: "'Inter', sans-serif" }),
  btn:     (accent, disabled) => ({ width: '100%', padding: '14px', background: disabled ? '#27272a' : accent, border: 'none', borderRadius: 12, color: disabled ? '#52525b' : '#fff', fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif", transition: 'all 0.2s' }),
  skipBtn: { background: 'none', border: 'none', color: '#52525b', fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif", padding: '8px 0', width: '100%', textAlign: 'center' },
  knowCard:{ background: '#18181b', border: '1px solid #27272a', borderRadius: 12, padding: '16px 18px' },
  knowRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #27272a' },
  photoBox:{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', cursor: 'pointer', transition: 'all 0.15s' },
  progress:(pct, accent) => ({ height: 3, background: '#18181b', marginBottom: 28 }),
  progressBar:(pct, accent) => ({ height: '100%', width: `${pct}%`, background: accent, transition: 'width 0.4s ease', borderRadius: 2 }),
};

const INTEREST_GROUPS = {
  '🏃 Active & Outdoors': ['Hiking','Running','Cycling','Yoga','Swimming','Surfing','Camping','Tennis','Golf','Rock Climbing','AFL','NRL & Rugby League','Rugby Union','Touch Football','Soccer','Cricket','Netball','Basketball','Hockey','Golf','Boxing','BJJ','CrossFit','Paddleboarding','Kayaking','Fishing','Horse Riding','Triathlon'],
  '🎨 Arts & Creative':   ['Photography','Painting','Drawing','Film & Cinema','Theatre','Dance','Music','Writing'],
  '🍳 Food & Drink':      ['Cooking Classes','Coffee Meetups','Wine Tasting','Food Tours','Baking','Markets'],
  '🎮 Social & Games':    ['Board Games','Trivia','Escape Rooms','Pub Nights','Karaoke'],
  '📚 Learning & Growth': ['Book Clubs','Workshops','Tech Meetups','Language Exchange','Public Speaking'],
  '🌿 Wellness':          ['Meditation','Pilates','Mental Health','Breathwork','Journaling'],
  '🤝 Community':         ['Volunteering','Networking','Environmental','Social Causes','Cultural Events'],
};

// ── Step: Confirm what we already know ────────────────────────────────────────
function KnowStep({ alreadyKnow, accentColor, onConfirm, onSkip }) {
  const keys = Object.keys(alreadyKnow).filter(k => k !== 'photoCount');
  const LABELS = { name: 'Name', location: 'Location', bio: 'About you', occupation: 'Occupation', interests: 'Interests', goals: 'Goals' };

  return (
    <div style={S.card}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>👋</div>
        <h2 style={S.title}>Welcome back</h2>
        <p style={{ ...S.sub, marginTop: 8 }}>We already know a few things about you. Want to use this here?</p>
      </div>
      <div style={S.knowCard}>
        {keys.map((k, i) => {
          const val = alreadyKnow[k];
          const display = k === 'location' ? (val.display || val.city || val.suburb)
            : Array.isArray(val) ? val.slice(0,3).join(', ') + (val.length > 3 ? '…' : '')
            : String(val).slice(0, 60);
          return (
            <div key={k} style={{ ...S.knowRow, borderBottom: i < keys.length - 1 ? '1px solid #27272a' : 'none' }}>
              <span style={{ color: '#a1a1aa', fontSize: 13 }}>{LABELS[k] || k}</span>
              <span style={{ color: '#fafafa', fontSize: 13, fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
            </div>
          );
        })}
        {alreadyKnow.photoCount > 0 && (
          <div style={{ ...S.knowRow, borderBottom: 'none' }}>
            <span style={{ color: '#a1a1aa', fontSize: 13 }}>Photos</span>
            <span style={{ color: '#fafafa', fontSize: 13, fontWeight: 500 }}>{alreadyKnow.photoCount} available</span>
          </div>
        )}
      </div>
      <button style={S.btn(accentColor, false)} onClick={onConfirm}>Yes, use this</button>
      <button style={S.skipBtn} onClick={onSkip}>I'll set this up later</button>
    </div>
  );
}

// ── Step: Location ─────────────────────────────────────────────────────────────
function LocationStep({ data, setData, accentColor, setCanProceed }) {
  useEffect(() => { setCanProceed(!!(data.suburb || data.city)); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={S.card}>
      <h2 style={S.title}>Where are you based?</h2>
      <p style={S.sub}>We'll use this to show you relevant local events and discussions.</p>
      <div>
        <label style={S.label}>Suburb</label>
        <input style={S.input} placeholder="e.g. North Ward" value={data.suburb || ''} onChange={e => setData({ ...data, suburb: e.target.value })} />
      </div>
      <div>
        <label style={S.label}>City</label>
        <input style={S.input} placeholder="e.g. Townsville" value={data.city || ''} onChange={e => setData({ ...data, city: e.target.value })} />
      </div>
      <div>
        <label style={S.label}>State</label>
        <select style={S.input} value={data.state || ''} onChange={e => setData({ ...data, state: e.target.value })}>
          <option value="">Select state</option>
          {['QLD','NSW','VIC','SA','WA','TAS','ACT','NT'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Step: Interests ────────────────────────────────────────────────────────────
function InterestsStep({ data, setData, accentColor, setCanProceed, onRequestInterest, apiBase, token }) {
  const selected = data.interests || [];
  const [search, setSearch] = useState('');
  const [showRequest, setShowRequest] = useState(false);
  const [spellCheck, setSpellCheck] = useState(null); // { ok, corrected }
  const [checking, setChecking] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState('');

  useEffect(() => { setCanProceed(selected.length >= 3); }, [selected.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flatten all interests for search
  const ALL_FLAT = Object.values(INTEREST_GROUPS).flat();

  const filtered = search.trim().length > 0
    ? ALL_FLAT.filter(i => i.toLowerCase().includes(search.toLowerCase()))
    : null;

  // Check if search term has zero results
  const noResults = filtered !== null && filtered.length === 0;
  const hasSearch = search.trim().length > 0;

  const toggle = (i) => {
    const next = selected.includes(i) ? selected.filter(x => x !== i) : [...selected, i];
    setData({ ...data, interests: next });
    setSearch('');
    setShowRequest(false);
    setSpellCheck(null);
  };

  // Run spell check when user clicks the + button
  const handleRequestClick = async () => {
    if (!search.trim()) return;
    setChecking(true);
    setShowRequest(false);
    setSpellCheck(null);
    try {
      const r = await fetch(`${apiBase}/api/interest-requests/spell-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: search.trim() })
      }).then(x => x.json());
      setSpellCheck(r.data);
    } catch { setSpellCheck({ ok: true, corrected: null }); }
    setChecking(false);
    setShowRequest(true);
  };

  const applyCorrection = () => {
    if (spellCheck?.corrected) {
      setSearch(spellCheck.corrected);
      setSpellCheck(null);
    }
  };

  const submitRequest = async () => {
    const finalText = spellCheck?.corrected || search.trim();
    if (!finalText || submitting) return;
    setSubmitting(true);
    try {
      await fetch(`${apiBase}/api/interest-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requested: finalText, context: context.trim() })
      }).then(x => x.json());
      setSubmitted(true);
    } catch {}
    setSubmitting(false);
  };

  const resetRequest = () => {
    setShowRequest(false);
    setSubmitted(false);
    setSpellCheck(null);
    setContext('');
    setSearch('');
  };

  return (
    <div style={S.card}>
      <h2 style={S.title}>What are you into?</h2>
      <p style={S.sub}>Pick at least 3. Can't find something? Search and request it.</p>

      {/* Search bar */}
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...S.input, paddingRight: hasSearch ? 44 : 14 }}
          placeholder="Search interests..."
          value={search}
          onChange={e => { setSearch(e.target.value); setShowRequest(false); setSubmitted(false); setSpellCheck(null); }}
        />
        {/* + button — appears when search has no matches */}
        {noResults && !showRequest && (
          <button
            onClick={handleRequestClick}
            title="Request this interest"
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, borderRadius: '50%', background: accentColor, border: 'none', color: '#fff', fontSize: 18, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif" }}>
            {checking ? '…' : '+'}
          </button>
        )}
      </div>

      {/* Request flow — shown after + is clicked */}
      {showRequest && !submitted && (
        <div style={{ background: '#18181b', border: `1px solid ${accentColor}33`, borderRadius: 12, padding: '14px 16px', marginTop: 4 }}>
          {/* Spell check correction */}
          {spellCheck && !spellCheck.ok && (
            <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13 }}>
              <span style={{ color: '#fbbf24' }}>✏️ Did you mean </span>
              <button onClick={applyCorrection} style={{ background: 'none', border: 'none', color: accentColor, fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: "'Inter', sans-serif", padding: 0 }}>
                "{spellCheck.corrected}"
              </button>
              <span style={{ color: '#71717a' }}>?</span>
            </div>
          )}

          <div style={{ fontSize: 14, fontWeight: 600, color: '#fafafa', marginBottom: 6 }}>
            "{spellCheck?.corrected || search}" wasn't found
          </div>
          <div style={{ fontSize: 13, color: '#71717a', marginBottom: 10, lineHeight: 1.5 }}>
            Would you like to request it be added? Our AI team will review it.
            If approved, it'll be added to your profile automatically — and you'll earn <span style={{ color: accentColor }}>5 growth points</span>.
          </div>
          <textarea
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="Why should this be added? (optional)"
            rows={2}
            style={{ ...S.textarea, marginBottom: 10, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submitRequest} disabled={submitting}
              style={{ ...S.btn(accentColor, submitting), flex: 1, padding: '10px' }}>
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
            <button onClick={resetRequest}
              style={{ background: 'none', border: '1px solid #27272a', borderRadius: 10, padding: '10px 14px', color: '#71717a', cursor: 'pointer', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Submitted confirmation */}
      {submitted && (
        <div style={{ background: '#18181b', border: `1px solid ${accentColor}33`, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>✅</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fafafa', marginBottom: 4 }}>Request submitted!</div>
          <div style={{ fontSize: 12, color: '#71717a', lineHeight: 1.5, marginBottom: 10 }}>
            We'll notify you when it's reviewed. If approved it'll be added to your interests automatically.
          </div>
          <button onClick={resetRequest}
            style={{ background: 'none', border: 'none', color: accentColor, fontSize: 13, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
            Keep searching
          </button>
        </div>
      )}

      {/* Interest chips — filtered or grouped */}
      {!showRequest && !submitted && (
        <>
          {filtered !== null && filtered.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: '35vh', overflowY: 'auto' }}>
              {filtered.map(item => (
                <div key={item} onClick={() => toggle(item)} style={S.chip(selected.includes(item), accentColor)}>{item}</div>
              ))}
            </div>
          ) : filtered === null ? (
            <div style={{ maxHeight: '40vh', overflowY: 'auto', paddingRight: 4 }}>
              {Object.entries(INTEREST_GROUPS).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 14 }}>
                  <p style={{ color: '#52525b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>{cat}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {items.map(item => (
                      <div key={item} onClick={() => toggle(item)} style={S.chip(selected.includes(item), accentColor)}>{item}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}

      {/* Selected count + hint */}
      {!submitted && (
        <p style={{ color: '#3f3f46', fontSize: 11, margin: 0 }}>
          {selected.length} selected {selected.length < 3 ? `— pick ${3 - selected.length} more` : '✓'}
        </p>
      )}
    </div>
  );
}

// ── Step: Bio (optional) ───────────────────────────────────────────────────────
function BioStep({ data, setData, accentColor, setCanProceed }) {
  useEffect(() => { setCanProceed(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={S.card}>
      <h2 style={S.title}>About you</h2>
      <p style={S.sub}>A short intro shown on your profile. What do you enjoy, what are you looking for?</p>
      <textarea style={S.textarea} value={data.bio || ''} onChange={e => setData({ ...data, bio: e.target.value })}
        placeholder="e.g. I love hiking and coffee. Recently moved to Townsville and keen to meet people..." rows={4} maxLength={300} />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#3f3f46', fontSize: 11 }}>Optional — you can add this later</span>
        <span style={{ color: '#52525b', fontSize: 11 }}>{(data.bio || '').length}/300</span>
      </div>
    </div>
  );
}

// ── Step: Photos ───────────────────────────────────────────────────────────────
function PhotosStep({ existingPhotos, appPhotoSelections, app, data, setData, accentColor, apiBase, token, setCanProceed }) {
  const [photos, setPhotos] = useState(existingPhotos || []);
  const [selected, setSelected] = useState(() => new Set(appPhotoSelections || []));
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setCanProceed(true); // Photos are optional
    setData(d => ({ ...d, _photoSelections: [...selected], _photos: photos }));
  }, [selected, photos]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= 6) return alert('Maximum 6 photos');
    setUploading(true);
    const form = new FormData();
    form.append('photo', file);
    const r = await fetch(API(apiBase, '/narbodica-profile/photos'), {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    }).then(x => x.json()).catch(() => null);
    setUploading(false);
    if (r?.success) {
      setPhotos(r.data.photos);
      setSelected(prev => { const next = new Set(prev); next.add(r.data.photos.length - 1); return next; });
    }
  };

  return (
    <div style={S.card}>
      <h2 style={S.title}>Your photos</h2>
      <p style={S.sub}>
        {photos.length > 0
          ? `You have ${photos.length} photo${photos.length !== 1 ? 's' : ''} on your Narbodica profile. Select which ones to show here.`
          : 'Add a photo so people can recognise you. You can add up to 6 across all your apps.'}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {photos.map((p, i) => (
          <div key={p._id || i} onClick={() => toggleSelect(i)} style={{ position: 'relative', cursor: 'pointer' }}>
            <img src={p.thumbUrl || p.url} alt="" style={{ ...S.photoBox, border: `3px solid ${selected.has(i) ? accentColor : 'transparent'}`, opacity: selected.has(i) ? 1 : 0.6 }} />
            {selected.has(i) && (
              <div style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>✓</div>
            )}
          </div>
        ))}
        {photos.length < 6 && (
          <label style={{ ...S.photoBox, border: '2px dashed #3f3f46', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#18181b' }}>
            <span style={{ color: '#52525b', fontSize: 24 }}>{uploading ? '⏳' : '+'}</span>
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={upload} disabled={uploading} />
          </label>
        )}
      </div>

      <p style={{ color: '#3f3f46', fontSize: 11, margin: 0 }}>
        {photos.length}/6 photos · {selected.size} selected for this app
      </p>
    </div>
  );
}

// ── Request Interest Modal ────────────────────────────────────────────────────
function RequestInterestModal({ accentColor, apiBase, token, onClose }) {
  const [value, setValue] = useState('');
  const [context, setContext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!value.trim() || submitting) return;
    setSubmitting(true);
    const r = await fetch(`${apiBase}/api/interest-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ requested: value.trim(), context: context.trim() })
    }).then(x => x.json()).catch(() => null);
    setSubmitting(false);
    if (r?.success) {
      setResult(r.data);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ width: '100%', background: '#18181b', borderRadius: '16px 16px 0 0', padding: '20px 16px 32px', maxWidth: 480, margin: '0 auto' }}>
        {result ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              {result.status === 'pending' ? '⏳' : result.duplicate ? '✅' : '📬'}
            </div>
            <h3 style={{ color: '#fafafa', fontSize: 18, fontFamily: "var(--font-serif)", margin: '0 0 8px' }}>
              {result.duplicate ? 'Already submitted' : 'Request received!'}
            </h3>
            <p style={{ color: '#71717a', fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>
              {result.duplicate
                ? `You've already requested this — we'll let you know the outcome.`
                : `Our team will review your request. You'll earn 5 growth points if it's approved.`}
            </p>
            <button onClick={onClose} style={{ ...S.btn(accentColor, false), width: 'auto', padding: '10px 24px' }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fafafa', fontFamily: "'Inter', sans-serif" }}>Request a new interest</span>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
            <input value={value} onChange={e => setValue(e.target.value)}
              placeholder="e.g. Rugby League, Underwater Hockey..."
              style={{ ...S.input, marginBottom: 10 }} />
            <textarea value={context} onChange={e => setContext(e.target.value)}
              placeholder="Why should this be added? (optional)"
              rows={2} style={{ ...S.textarea, marginBottom: 14 }} />
            <p style={{ color: '#52525b', fontSize: 11, marginBottom: 14, lineHeight: 1.5 }}>
              Our AI team reviews all requests. If approved you'll earn <span style={{ color: accentColor }}>5 growth points</span>. If it already exists under another name, we'll point you to it.
            </p>
            <button onClick={submit} disabled={!value.trim() || submitting}
              style={S.btn(accentColor, !value.trim() || submitting)}>
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function NarbodicaProfileOnboarding({ app, appLabel, accentColor = '#8b5cf6', apiBase, token, onComplete, onSkip }) {
  const [loading, setLoading]     = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [step, setStep]           = useState(0); // 0=know, 1..n=missing fields, last=photos
  const [formData, setFormData]   = useState({});
  const [canProceed, setCanProceed] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Default onSkip — if caller doesn't provide one, just complete onboarding
  const handleSkip = onSkip || onComplete;

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(API(apiBase, `/narbodica-profile/for-app/${app}`), {
      headers: { Authorization: `Bearer ${token}` }
    }).then(x => x.json()).catch(() => null);
    if (r?.success) {
      setProfileData(r.data);
      // Pre-fill form with existing data
      const p = r.data.profile;
      setFormData({
        location:  p.location || {},
        interests: p.interests || [],
        bio:       p.bio || '',
        occupation: p.occupation || '',
      });
    }
    setLoading(false);
  }, [app, apiBase, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ ...S.page }}>
        <div style={{ color: '#3f3f46', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>Setting up your profile...</div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div style={{ ...S.page }}>
        <div style={{ color: '#f87171', fontSize: 13 }}>Could not load profile. Please try again.</div>
        <button onClick={load} style={{ marginTop: 12, ...S.btn(accentColor, false), width: 'auto', padding: '10px 20px' }}>Retry</button>
      </div>
    );
  }

  const { alreadyKnow, missingFields, photos, profile } = profileData;
  const hasKnownData = Object.keys(alreadyKnow).length > 0;

  // Build steps dynamically based on what's missing
  const steps = [];
  if (hasKnownData && step === 0) {
    // Confirmation step shown first
  }
  if (missingFields.includes('location.city')) steps.push('location');
  if (missingFields.includes('interests'))     steps.push('interests');
  if (missingFields.includes('bio'))           steps.push('bio');
  steps.push('photos'); // always show photos step

  // Effective step: 0 = know confirmation (if has data), then step 1..n = missing fields
  const showKnowStep = hasKnownData && step === 0;
  const fieldStep    = hasKnownData ? step - 1 : step;
  const currentField = steps[fieldStep];
  const totalSteps   = (hasKnownData ? 1 : 0) + steps.length;
  const currentStepNum = step + 1;
  const pct = (currentStepNum / totalSteps) * 100;

  const next = async () => {
    if (step < totalSteps - 1) {
      setStep(s => s + 1);
      setCanProceed(false);
    } else {
      await complete();
    }
  };

  const complete = async () => {
    setSaving(true);
    setError('');

    // Build additional data to write back to NarbodicaProfile
    const additionalData = {};
    if (formData.location?.city || formData.location?.suburb) {
      additionalData.location = {
        ...formData.location,
        display: [formData.location.suburb, formData.location.city, formData.location.state].filter(Boolean).join(', ')
      };
    }
    if (formData.interests?.length)  additionalData.interests  = formData.interests;
    if (formData.bio?.trim())        additionalData.bio        = formData.bio.trim();
    if (formData.occupation?.trim()) additionalData.occupation = formData.occupation.trim();

    // Save photo selections
    if (formData._photoSelections?.length) {
      const selections = profile.appPhotoSelections || {};
      selections[app] = formData._photoSelections;
      await fetch(API(apiBase, '/narbodica-profile'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ appPhotoSelections: selections }),
      }).then(x => x.json()).catch(() => null);
    }

    // Mark onboarding complete + write back new data
    const r = await fetch(API(apiBase, `/narbodica-profile/complete-app-onboarding/${app}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ additionalData }),
    }).then(x => x.json()).catch(() => null);

    setSaving(false);
    if (r?.success) {
      onComplete();
    } else {
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div style={S.page}>
      {/* Request Interest Modal */}
      {showRequestModal && (
        <RequestInterestModal
          accentColor={accentColor}
          apiBase={apiBase}
          token={token}
          onClose={() => setShowRequestModal(false)}
        />
      )}

      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=DM+Serif+Display&display=swap');
        input:focus, textarea:focus, select:focus { border-color: ${accentColor} !important; }
        select option { background: #18181b; color: #fafafa; }
      `}</style>

      <div style={{ width: '100%', maxWidth: 480, marginBottom: 4 }}>
        {/* Progress bar */}
        <div style={S.progress(pct, accentColor)}>
          <div style={S.progressBar(pct, accentColor)} />
        </div>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: accentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff' }}>
              {appLabel[0]}
            </div>
            <span style={{ color: accentColor, fontFamily: "var(--font-serif)", fontSize: 18 }}>{appLabel}</span>
          </div>
        </div>
      </div>

      {/* Know confirmation step */}
      {showKnowStep && (
        <KnowStep alreadyKnow={alreadyKnow} accentColor={accentColor}
          onConfirm={() => { setStep(1); setCanProceed(false); }}
          onSkip={handleSkip} />
      )}

      {/* Field steps */}
      {!showKnowStep && currentField === 'location' && (
        <LocationStep data={formData.location || {}} setData={loc => setFormData(d => ({ ...d, location: loc }))}
          accentColor={accentColor} setCanProceed={setCanProceed} />
      )}
      {!showKnowStep && currentField === 'interests' && (
        <InterestsStep data={formData} setData={setFormData} accentColor={accentColor} setCanProceed={setCanProceed}
          apiBase={apiBase} token={token}
          onRequestInterest={() => setShowRequestModal(true)} />
      )}
      {!showKnowStep && currentField === 'bio' && (
        <BioStep data={formData} setData={setFormData} accentColor={accentColor} setCanProceed={setCanProceed} />
      )}
      {!showKnowStep && currentField === 'photos' && (
        <PhotosStep existingPhotos={photos} appPhotoSelections={profile.appPhotoSelections?.[app]}
          app={app} data={formData} setData={setFormData} accentColor={accentColor}
          apiBase={apiBase} token={token} setCanProceed={setCanProceed} />
      )}

      {/* Navigation */}
      {!showKnowStep && (
        <div style={{ width: '100%', maxWidth: 480, marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {error && <p style={{ color: '#f87171', fontSize: 13, textAlign: 'center', margin: 0 }}>{error}</p>}
          <button style={S.btn(accentColor, (!canProceed && currentField !== 'bio' && currentField !== 'photos') || saving)}
            onClick={next} disabled={(!canProceed && currentField !== 'bio' && currentField !== 'photos') || saving}>
            {saving ? 'Saving...' : fieldStep >= steps.length - 1 ? `Start using ${appLabel}` : 'Continue'}
          </button>
          {currentField !== 'photos' && fieldStep < steps.length - 1 && (
            <button style={S.skipBtn} onClick={next}>Skip for now</button>
          )}
        </div>
      )}
      {/* Request Interest Modal */}
      {showRequestModal && (
        <RequestInterestModal
          accentColor={accentColor}
          apiBase={apiBase}
          token={token}
          onClose={() => setShowRequestModal(false)}
        />
      )}
    </div>
  );
}